import axios, { type AxiosInstance } from 'axios';

/**
 * HTTP client for the portal admin API.
 *
 * Every call is authenticated as BCE staff; there is no anonymous admin surface.
 * The token is attached by an interceptor rather than per call so a new endpoint
 * cannot accidentally be added unauthenticated.
 */
export const api: AxiosInstance = axios.create({
  baseURL: '/api/v3',
  headers: { Accept: 'application/json' },
  timeout: 20_000,
});

let getToken: (() => Promise<string | null>) | null = null;
/** The full `Authorization` value; set by {@link setTokenProvider} or {@link setAuthorizationProvider}. */
let getAuthorization: (() => Promise<string | null>) | null = null;

/**
 * Registers the function that supplies the staff bearer token.
 *
 * Injected from the auth layer rather than imported, so this module has no
 * dependency on Clerk and stays trivially testable.
 *
 * @param provider - Returns the current token, or null when signed out.
 */
export function setTokenProvider(provider: () => Promise<string | null>): void {
  getToken = provider;
  getAuthorization = async () => {
    const token = await provider();
    return token ? `Bearer ${token}` : null;
  };
}

/**
 * Registers a provider of the whole `Authorization` header value, for a
 * surface whose credential is not a staff bearer token — the proposal editor
 * embedded in Salesforce sends `Editor <token>`. There is then no staff
 * token: {@link currentStaffToken} answers null.
 *
 * @param provider - Returns the header value, or null to send none.
 */
export function setAuthorizationProvider(provider: () => Promise<string | null>): void {
  getToken = null;
  getAuthorization = provider;
}

/**
 * The current staff bearer token, for callers that build their own requests
 * (the V3 proxy layer). Null when signed out.
 *
 * @returns The token, or null.
 */
export async function currentStaffToken(): Promise<string | null> {
  return getToken ? getToken() : null;
}

/**
 * The `Authorization` header for a request built outside {@link api}, or
 * none. Use this rather than `currentStaffToken` so the request also works
 * from the embedded editor.
 *
 * @returns Headers to spread into a request.
 */
export async function authorizationHeader(): Promise<Record<string, string>> {
  const value = getAuthorization ? await getAuthorization() : null;
  return value ? { Authorization: value } : {};
}

api.interceptors.request.use(async (requestConfig) => {
  const value = getAuthorization ? await getAuthorization() : null;
  if (value) {
    requestConfig.headers.Authorization = value;
  }
  return requestConfig;
});

/** Envelope every admin API success response uses. */
interface ApiSuccess<T> {
  readonly success: true;
  readonly data: T;
}

/** Pagination metadata returned alongside list responses. */
export interface PaginationMeta {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
  readonly totalPages: number;
  readonly hasNext: boolean;
  readonly hasPrev: boolean;
}

interface ApiListSuccess<T> extends ApiSuccess<T[]> {
  readonly pagination: PaginationMeta;
}

/**
 * GETs a resource and unwraps the envelope.
 *
 * @param path - Path relative to the admin API base.
 * @param params - Query parameters.
 * @param signal - Abort signal from React Query.
 * @returns The unwrapped payload.
 */
export async function getJson<T>(
  path: string,
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const response = await api.get<ApiSuccess<T>>(path, {
    ...(params ? { params } : {}),
    ...(signal ? { signal } : {}),
  });
  return response.data.data;
}

/**
 * GETs a paginated list, keeping the pagination metadata.
 *
 * @param path - Path relative to the admin API base.
 * @param params - Query parameters.
 * @param signal - Abort signal from React Query.
 * @returns Items and pagination.
 */
export async function getList<T>(
  path: string,
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<{ items: T[]; pagination: PaginationMeta }> {
  const response = await api.get<ApiListSuccess<T>>(path, {
    ...(params ? { params } : {}),
    ...(signal ? { signal } : {}),
  });
  return { items: response.data.data, pagination: response.data.pagination };
}

/**
 * Sends a write request and unwraps the envelope.
 *
 * @param method - HTTP method.
 * @param path - Path relative to the admin API base.
 * @param body - Request payload.
 * @returns The unwrapped payload.
 */
export async function sendJson<T>(
  method: 'post' | 'patch' | 'put' | 'delete',
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await api.request<ApiSuccess<T>>({ method, url: path, data: body });
  return response.data.data;
}

/** A stored asset, as returned by the media upload endpoint. */
export interface UploadedMedia {
  readonly id: string;
  readonly url: string;
  readonly provider: string;
  readonly location: string;
  readonly contentType: string;
  readonly size: number;
  readonly kind: string | null;
  readonly organizationId: string | null;
}

/**
 * Uploads an image to S3 via the API and returns its stored URL.
 *
 * Multipart so the browser never sees an AWS credential — the API holds the
 * bucket. The auth interceptor attaches the staff token; axios sets the
 * multipart boundary itself from the FormData.
 *
 * @param file - The image to upload.
 * @param opts - Owning organization and what the asset is used as.
 * @returns The stored media object, including its public URL.
 */
export async function uploadMedia(
  file: File,
  opts?: { organizationId?: string; kind?: string },
): Promise<UploadedMedia> {
  const form = new FormData();
  form.append('file', file);
  if (opts?.organizationId) form.append('organizationId', opts.organizationId);
  if (opts?.kind) form.append('kind', opts.kind);
  const response = await api.post<ApiSuccess<UploadedMedia>>('/media', form);
  return response.data.data;
}

/**
 * Extracts the API's machine-readable error code from a thrown error.
 *
 * Lets callers distinguish "not a BCE admin" from "the network is down", which
 * need very different screens.
 *
 * @param error - Anything thrown by an API call.
 * @returns The error code, or null if this was not a structured API failure.
 */
export function apiErrorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const body = error.response?.data as { code?: string } | undefined;
  return typeof body?.code === 'string' ? body.code : null;
}

/**
 * Turns a thrown error into a message worth showing.
 *
 * The API's failure envelope carries a human-readable reason — "The slug
 * \"api\" is reserved" — which is far more useful than "Request failed with
 * status code 400".
 *
 * @param error - Anything thrown by an API call.
 * @returns A user-facing message.
 */
export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: string; details?: unknown } | undefined;
    if (typeof body?.error === 'string') {
      // A validation failure names the fields, so the person knows what to fix.
      const fields = Array.isArray(body.details)
        ? body.details
            .map((issue) => (issue && typeof issue === 'object' && Array.isArray((issue as { path?: unknown }).path) ? (issue as { path: unknown[] }).path.join('.') : ''))
            .filter((path) => path.length > 0)
        : [];
      return fields.length > 0 ? `${body.error}: ${[...new Set(fields)].join(', ')}` : body.error;
    }
    if (error.response?.status === 403) return 'Your account does not have permission to do that.';
    if (!error.response) return 'Could not reach the portal API.';
  }
  return error instanceof Error ? error.message : 'Something went wrong.';
}
